import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FreshCatch {
  id: string;
  name: string;
  image: string;
  alt: string;
  price: number;
  originalPrice?: number;
  weight: string;
  catchDate: string;
  availability: 'in-stock' | 'low-stock' | 'out-of-stock';
  freshnessScore: number;
  location: string;
}

interface TodaysFreshCatchProps {
  products: FreshCatch[];
}

const TodaysFreshCatch = ({ products }: TodaysFreshCatchProps) => {
  const getAvailabilityBadge = (availability: string) => {
    switch (availability) {
      case 'in-stock':
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
            <Icon name="CheckCircleIcon" size={14} />
            In Stock
          </span>
        );
      case 'low-stock':
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-warning/10 text-warning rounded-full text-xs font-medium">
            <Icon name="ExclamationCircleIcon" size={14} />
            Low Stock
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-error/10 text-error rounded-full text-xs font-medium">
            <Icon name="XCircleIcon" size={14} />
            Out of Stock
          </span>
        );
    }
  };

  return (
    <section className="py-12 px-6 bg-muted">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl font-bold text-foreground mb-2">
              Today's Fresh Catch
            </h2>
            <p className="text-muted-foreground">
              Just arrived from the sea this morning
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-success/10 text-success rounded-md">
            <Icon name="SparklesIcon" size={18} />
            <span className="font-medium text-sm">Updated 2 hours ago</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((product) => (
            <Link
              key={product.id}
              href="/product-listing"
              className="group bg-card rounded-lg overflow-hidden shadow-elevation-sm hover:shadow-elevation-md transition-smooth hover-lift"
            >
              <div className="relative h-56 overflow-hidden">
                <AppImage
                  src={product.image}
                  alt={product.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-smooth"
                />
                <div className="absolute top-3 left-3">
                  <span className="flex items-center gap-1 px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-bold">
                    <Icon name="FireIcon" size={14} />
                    Fresh Today
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  {getAvailabilityBadge(product.availability)}
                </div>
              </div>

              <div className="p-4">
                <h3 className="font-heading text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-smooth">
                  {product.name}
                </h3>

                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Icon
                        key={i}
                        name="StarIcon"
                        size={14}
                        variant={i < Math.floor(product.freshnessScore / 20) ? 'solid' : 'outline'}
                        className={i < Math.floor(product.freshnessScore / 20) ? 'text-warning' : 'text-muted-foreground'}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {product.freshnessScore}% Fresh
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                  <Icon name="MapPinIcon" size={14} />
                  <span>{product.location}</span>
                </div>

                <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                  <Icon name="CalendarIcon" size={14} />
                  <span>Caught: {product.catchDate}</span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-data text-2xl font-bold text-primary">
                        ₹{product.price}
                      </span>
                      {product.originalPrice && (
                        <span className="font-data text-sm text-muted-foreground line-through">
                          ₹{product.originalPrice}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">per {product.weight}</span>
                  </div>
                  <button className="p-2 bg-primary text-primary-foreground rounded-md transition-smooth hover:shadow-elevation-md hover-lift">
                    <Icon name="ShoppingCartIcon" size={20} />
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="flex justify-center mt-8">
          <Link
            href="/product-listing"
            className="flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-smooth hover:shadow-elevation-md hover-lift"
          >
            <span>View All Fresh Catch</span>
            <Icon name="ArrowRightIcon" size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TodaysFreshCatch;